/**
 * Child Authentication Service
 * Handles child login with Supabase anonymous auth and direct RLS access
 */

import { supabase, supabaseRpc } from '@/lib/supabase'
import { todayStr } from '@/services/date.service'
import { useAnalytics } from '@/composables/useAnalytics'

const analytics = useAnalytics()

interface ChildData {
  id: string
  name: string
  householdId: string
  avatar: string | null
  points: number
  currentLevel: number
  permissions: ChildPermissions
}

interface ChildPermissions {
  can_mark_tasks_done: boolean
  can_customize_avatar: boolean
  can_view_rewards_catalog: boolean
  can_redeem_rewards: boolean
  can_see_other_children_progress: boolean
  can_review_other_children_tasks: boolean
}

interface LoginResponse {
  success: boolean
  child: ChildData
}

interface TaskWithStatus {
  id: string
  title: string
  description: string
  points: number
  icon: string | null
  isCompleted: boolean
  submissionStatus: 'pending' | 'approved' | 'rejected' | null
  submissionId: string | null
  scheduled_time?: string | null
}

interface RewardWithProgress {
  id: string
  title: string
  description: string
  required_points: number
  icon: string | null
  image_url: string | null
  progress: number
  isUnlocked: boolean
  collectedCount: number
  pointsNeeded: number
}

export const childAuthService = {
  /**
   * Link child to anonymous account
   */
  async linkChildToAccount(childId: string, authUserId: string): Promise<boolean> {
    const { data, error } = await supabaseRpc.rpc('link_child_to_anonymous_account', {
      p_auth_user_id: authUserId,
      p_child_id: childId
    })

    if (error) {
      throw new Error(error.message)
    }

    return data === true
  },

  /**
   * Get child data (requires authenticated session)
   */
  async getChildData(): Promise<ChildData | null> {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    // Get child linked to this auth user via children.auth_user_id
    const { data: child, error: childError } = await (supabase as any)
      .from('children')
      .select('*')
      .eq('auth_user_id', user.id)
      .maybeSingle()

    if (childError || !child) return null

    // Get permissions (table not in generated types)
    const { data: permissions } = await (supabase as any)
      .from('child_permissions')
      .select('*')
      .eq('child_id', child.id)
      .maybeSingle()

    return {
      id: child.id,
      name: child.name,
      householdId: child.household_id,
      avatar: child.avatar,
      points: child.points || 0,
      currentLevel: child.current_level || 1,
      permissions: {
        can_mark_tasks_done: permissions?.can_mark_tasks_done ?? true,
        can_customize_avatar: permissions?.can_customize_avatar ?? true,
        can_view_rewards_catalog: permissions?.can_view_rewards_catalog ?? true,
        can_redeem_rewards: permissions?.can_redeem_rewards ?? false,
        can_see_other_children_progress: permissions?.can_see_other_children_progress ?? false,
        can_review_other_children_tasks: permissions?.can_review_other_children_tasks ?? false
      }
    }
  },

  /**
   * Get tasks for the child (requires authenticated session)
   */
  async getTasks(date?: string): Promise<{ tasks: TaskWithStatus[] }> {
    const targetDate = date || todayStr()

    // Get child ID from auth user via children.auth_user_id
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data: child } = await (supabase as any)
      .from('children')
      .select('id, household_id')
      .eq('auth_user_id', user.id)
      .maybeSingle()

    if (!child) throw new Error('Child not linked to account')

    const { id: childId, household_id: householdId } = child as { id: string; household_id: string }

    // Le filtre household_id n'est pas de la sécurité (la RLS s'en charge) : sans
    // lui, `assignment.eq.all` fait lire à Postgres les tâches de TOUS les foyers,
    // que la RLS écarte ensuite une par une (HARMONIA-78).
    const { data: tasks, error: tasksError } = await supabase
      .from('tasks')
      .select('*')
      .eq('household_id', householdId)
      .eq('is_active', true)
      // Sous-ensemble d'enfants (#406) : `children_ids` le contient.
      .or(`assignment.eq.all,and(assignment.eq.specific,child_id.eq.${childId}),children_ids.cs.{${childId}}`)

    if (tasksError) throw tasksError

    // Get daily completions
    const { data: completion } = await supabase
      .from('daily_completions')
      .select('tasks_data')
      .eq('child_id', childId)
      .eq('date', targetDate)
      .single()

    // Get pending submissions (table not in generated types)
    const { data: submissions } = await (supabase as any)
      .from('child_task_submissions')
      .select('*')
      .eq('child_id', childId)
      .eq('date', targetDate)

    const submissionsMap = new Map(
      (submissions || []).map((s: any) => [s.task_id, s])
    )

    const completionTasks = Array.isArray(completion?.tasks_data) ? completion.tasks_data : [];
    const completionsMap = new Map(
      completionTasks.map((t: any) => [t.task_id, t])
    )

    // Merge task data
    const tasksWithStatus: TaskWithStatus[] = (tasks || []).map((task: any) => {
      const taskCompletion = completionsMap.get(task.id)
      const submission = submissionsMap.get(task.id)

      return {
        id: task.id,
        title: task.title,
        description: task.description || '',
        points: task.points || 0,
        icon: task.icon,
        isCompleted: taskCompletion?.status === 'completed',
        submissionStatus: (submission as any)?.status ?? null,
        submissionId: (submission as any)?.id ?? null
      }
    })

    return { tasks: tasksWithStatus }
  },

  /**
   * Submit a task for parent approval (requires authenticated session)
   */
  async submitTask(taskId: string, date?: string): Promise<{ success: boolean; submissionId: string; message: string }> {
    const targetDate = date || todayStr()

    // Get child ID from auth user via children.auth_user_id
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data: child } = await (supabase as any)
      .from('children')
      .select('id')
      .eq('auth_user_id', user.id)
      .single()

    if (!child) throw new Error('Child not linked to account')

    // Insert submission (RLS will ensure child can only create for themselves)
    const { data, error } = await (supabase as any)
      .from('child_task_submissions')
      .insert({
        child_id: child.id,
        task_id: taskId,
        date: targetDate,
        status: 'pending'
      })
      .select('id')
      .single()

    if (error) {
      // Check if it's a conflict (already submitted)
      if (error.code === '23505') {
        // Get existing submission
        const { data: existing } = await (supabase as any)
          .from('child_task_submissions')
          .select('id')
          .eq('child_id', child.id)
          .eq('task_id', taskId)
          .eq('date', targetDate)
          .single()

        if (existing) {
          return {
            success: true,
            submissionId: existing.id,
            message: 'Task already submitted'
          }
        }
      }
      throw new Error(error.message)
    }

    // Côté mode enfant, la tâche est faite au moment où l'enfant la déclare.
    // La validation parentale qui suivra n'émettra pas un second
    // task_completed : elle verra une soumission approuvée et n'enverra que
    // task_validated. Le doublon de la branche 23505 (déjà soumise) est écarté
    // par le return anticipé plus haut.
    analytics.logTaskCompleted(taskId, undefined, undefined, child.id, undefined, {
      surface: 'child_mode',
      doneBy: 'child',
    })

    return {
      success: true,
      submissionId: (data as { id: string }).id,
      message: 'Task submitted for parent approval'
    }
  },

  /**
   * Cancel a pending task submission (requires authenticated session)
   */
  async cancelTaskSubmission(submissionId: string): Promise<{ success: boolean; message: string }> {
    // Get child ID from auth user via children.auth_user_id
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data: child } = await (supabase as any)
      .from('children')
      .select('id')
      .eq('auth_user_id', user.id)
      .single()

    if (!child) throw new Error('Child not linked to account')

    // Delete submission (RLS will ensure child can only delete their own pending submissions)
    const { error } = await (supabase as any)
      .from('child_task_submissions')
      .delete()
      .eq('id', submissionId)
      .eq('child_id', child.id)
      .eq('status', 'pending')

    if (error) {
      throw new Error(error.message)
    }

    return {
      success: true,
      message: 'Task submission cancelled'
    }
  },

  /**
   * Get rewards catalog (requires authenticated session)
   */
  async getRewards(): Promise<{ rewards: RewardWithProgress[]; currentPoints: number; collectedRewards: unknown[] }> {
    // Get child ID from auth user via children.auth_user_id
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data: child } = await (supabase as any)
      .from('children')
      .select('id, household_id, points')
      .eq('auth_user_id', user.id)
      .single()

    if (!child) throw new Error('Child not linked to account')

    const currentPoints = (child as any).points || 0

    // Get rewards (RLS will filter by household)
    const { data: rewards, error: rewardsError } = await supabase
      .from('rewards')
      .select('*')
      .eq('household_id', child.household_id)

    if (rewardsError) throw rewardsError

    // Get collected rewards
    const { data: collected } = await supabase
      .from('collected_rewards')
      .select('*')
      .eq('child_id', child.id)
      .eq('status', 'active')

    // Calculate progress for each reward
    const rewardsWithProgress: RewardWithProgress[] = (rewards || []).map((reward: any) => {
      const requiredPoints = reward.required_points || 0
      const progress = requiredPoints > 0
        ? Math.min(100, (currentPoints / requiredPoints) * 100)
        : 0
      const isUnlocked = currentPoints >= requiredPoints
      const collectedCount = (collected || []).filter((c: any) => c.reward_id === reward.id).length

      return {
        id: reward.id,
        title: reward.title,
        description: reward.description || '',
        required_points: requiredPoints,
        icon: reward.icon,
        image_url: reward.image_url,
        progress,
        isUnlocked,
        collectedCount,
        pointsNeeded: Math.max(0, requiredPoints - currentPoints)
      }
    })

    return {
      rewards: rewardsWithProgress,
      currentPoints,
      collectedRewards: collected || []
    }
  },

  /**
   * Get child profile for avatar customization (requires authenticated session)
   * Uses shop_items instead of animals table
   */
  async getProfile(): Promise<{
    child: unknown
    availableAnimals: unknown[]
    availableItems: unknown[]
    permissions: ChildPermissions
  }> {
    const childData = await this.getChildData()
    if (!childData) throw new Error('Child not found')

    // Get all avatar shop_items that are active
    const { data: avatarShopItems } = await supabase
      .from('shop_items' as any)
      .select('*')
      .eq('type', 'animal')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })

    // Get purchased avatar items for this child
    const { data: purchases } = await supabase
      .from('child_shop_purchases' as any)
      .select(`
        shop_item_id,
        shop_item:shop_items(
          id,
          type,
          metadata
        )
      `)
      .eq('child_id', childData.id)

    // Get currently equipped avatar from child_avatars
    // This ensures default avatars assigned during onboarding are considered available
    const { data: avatarConfig } = await (supabase as any)
      .from('child_avatars')
      .select('animal_id')
      .eq('child_id', childData.id)
      .single()

    // Create a set of purchased shop_item IDs
    const purchasedShopItemIds = new Set<string>()
    if (purchases) {
      purchases.forEach((purchase: any) => {
        if (purchase.shop_item?.type === 'animal' && purchase.shop_item_id) {
          purchasedShopItemIds.add(purchase.shop_item_id)
        }
      })
    }

    // Get the currently equipped animal ID
    const equippedAnimalId = (avatarConfig as { animal_id?: string } | null)?.animal_id || childData.avatar || null

    // Map shop_items to the format expected by ChildAvatarPage
    // Shop-only system: items are available if purchased OR if currently equipped (default avatar from onboarding)
    const availableAnimals = (avatarShopItems || []).map((shopItem: any) => {
      const metadata = (shopItem?.metadata || {}) as Record<string, unknown>
      const unlockLevel = metadata.unlock_level || 0
      const animalId = metadata.animal_id || shopItem.id
      const isPurchased = purchasedShopItemIds.has(shopItem.id)

      // Shop-only: available if purchased OR if currently equipped (default avatar from onboarding)
      // This ensures that avatars assigned during onboarding are always considered available
      const isCurrentlyEquipped = equippedAnimalId && animalId === equippedAnimalId
      const isAvailable = isPurchased || isCurrentlyEquipped

      return {
        id: metadata.animal_id || shopItem.id, // Use animal_id from metadata, fallback to shop_item id
        shop_item_id: shopItem.id, // Keep reference to shop_item for purchase tracking
        name: shopItem.name,
        description: shopItem.description,
        unlock_level: unlockLevel,
        locked: !isAvailable, // Locked if not purchased
        image_url: shopItem.image_url,
        metadata: metadata,
        rarity: shopItem.rarity,
        category: shopItem.category,
        price: shopItem.price,
        is_purchased: isPurchased,
        is_available: isAvailable // Include is_available flag for UI components
      }
    })

    // Get available items (these might need RLS policies too)
    const { data: items } = await supabase
      .from('avatar_items')
      .select('*')

    return {
      child: childData,
      availableAnimals,
      availableItems: items || [],
      permissions: childData.permissions
    }
  },

  /**
   * Update child avatar (requires authenticated session)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async updateAvatar(avatar?: string, avatarConfig?: Record<string, unknown>): Promise<{ success: boolean }> {
    // Get child ID from auth user via children.auth_user_id
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data: child } = await (supabase as any)
      .from('children')
      .select('id')
      .eq('auth_user_id', user.id)
      .single()

    if (!child) throw new Error('Child not linked to account')

    // Update child (RLS will ensure child can only update their own data)
    const updateData: any = {}
    if (avatar !== undefined) updateData.avatar = avatar

    const { error } = await (supabase as any)
      .from('children')
      .update(updateData)
      .eq('id', child.id)

    if (error) {
      throw new Error(error.message)
    }

    // TODO: Handle avatarConfig if needed (might require child_avatars table)

    return { success: true }
  }
}

// Export types
export type {
  ChildData,
  ChildPermissions,
  LoginResponse,
  TaskWithStatus,
  RewardWithProgress
}
